import { useState, useEffect } from 'react';

import { Handle, Position, useNodeId, useUpdateNodeInternals } from '@xyflow/react';
import { useGetRecordRepresentation, useTranslate } from 'react-admin';

import NodeIcon from '../../components/NodeIcon';
import NodeBodyWithDefaultConnection from '../NodeBodyWithDefaultConnection';
import NodeHeader from '../NodeHeader';
import NodeWrapper from '../NodeWrapper';

import type { UserType } from './UserControls';
import type { NodeProps } from '@xyflow/react';
import type { GetOneResult } from 'react-admin';

export default function UserNode({ data }: NodeProps & { data: UserType }): React.ReactElement {
  const [user, setUser] = useState<GetOneResult['data'] | null>(null);
  const translate = useTranslate();
  const getRecordRepresentation = useGetRecordRepresentation('users');
  const nodeId = useNodeId();
  const updateNodeInternals = useUpdateNodeInternals();

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (data.user !== undefined && data.user !== null) {
        setUser(data.user);
      } else {
        setUser(null);
      }
    };
    void fetchData();
  }, [data.user]);

  useEffect(() => {
    // Update node internals when instructions change or sorted with dnd
    if (nodeId !== null) {
      updateNodeInternals(nodeId);
    }
  }, [nodeId, data.instructions, updateNodeInternals]);

  const handles =
    data.instructions === undefined ||
    data.instructions.length === 0 ||
    data.instructions.find((instruction) => instruction.match_exit_causes.length > 0) === undefined
      ? []
      : data.instructions.map((instruction) => {
          return {
            id: instruction.id,
            value:
              typeof instruction.description === 'string' && instruction.description.length > 0
                ? instruction.description
                : instruction.match_exit_causes
                    .map((cause) =>
                      translate(`callflows.components.sortableInstructionsList.match_exit_causes.${cause}`)
                    )
                    .join(', '),
          };
        });

  const errors = data.user === undefined || data.user === null ? ['callflows.nodes.user.userIsNotSelected'] : [];

  return (
    <NodeWrapper errors={errors}>
      <NodeHeader
        icon={<NodeIcon name='user' />}
        title={user !== undefined ? (getRecordRepresentation(user) as string) : translate('callflows.nodes.user.name')}
        referencedResource={user?.id !== undefined ? { resource: 'users', id: user.id } : undefined}
      />
      <Handle type='target' position={Position.Left} id='user-target' />
      <NodeBodyWithDefaultConnection handles={handles} type={'user'} />
    </NodeWrapper>
  );
}
